import { create } from "zustand";
import type {
  IntakeFormData,
  IntakeFormMeta,
  FormStatus,
  SaveStatus,
  Mismatch,
  Owner,
  AffiliatedGroup,
  OwnershipAssignment,
  Employee,
  DocumentRef,
  IntakeFormResponse,
} from "../types";
import { TOTAL_STEPS, EMPTY_FORM_DATA } from "../types";
import { MAX_OWNERS } from "../types/structureConstants";
import { isRequiredDocsVisibleFor, isOwnerEligibilityVisible, isPriorBillVisible, isBinderCheckVisible, isAchDetailsVisible, DOC_VERIFICATION_SLOT } from "../hooks/step7Rules";
import { computeCompletedSteps } from "../validation";
import { getApi } from "../api";
import { trackWarning } from "../telemetry/appInsights";
import { saveCurrentDraft } from "../services/saveDraft";
import { useLockStore } from "./lockStore";

/**
 * Zustand store for all form state.
 *
 * Think of this like a static class that holds the form data,
 * current step, and save status. Any component can read from it
 * or call its actions, and React automatically re-renders when
 * the values they're using change. No prop-drilling needed.
 */

interface FormState {
  // Form identity
  formId: string | null;
  proposalId: string | null;
  /** Version number of the loaded form (1-based). */
  formVersion: number | null;
  formStatus: FormStatus;
  saveStatus: SaveStatus;
  isDirty: boolean;

  // Navigation
  currentStep: number;
  totalSteps: number;
  visitedSteps: Set<number>;

  // Form data
  data: IntakeFormData;

  // Baseline comparison — populated by the API when loading a form
  /** Which fields were pre-populated from the proposal/baseline. */
  prefilled: Record<string, boolean>;
  /** Snapshot of prefilled field values at load time — used for mismatch detection. */
  originalData: Record<string, unknown>;
  /** Fields where the user changed the value from the original. */
  mismatches: Record<string, Mismatch>;
  /** Server-sourced metadata (dropdown options, etc.) — populated by loadFromApi. */
  meta: IntakeFormMeta;

  // Actions
  loadFromApi: (response: IntakeFormResponse) => void;
  setField: <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setSaveStatus: (status: SaveStatus) => void;
  setFormStatus: (status: FormStatus) => void;
  // Array field actions
  addOwner: (owner: Owner) => void;
  updateOwner: (index: number, owner: Owner) => void;
  removeOwner: (index: number) => void;
  addAffiliatedGroup: (group: AffiliatedGroup) => void;
  updateAffiliatedGroup: (index: number, group: AffiliatedGroup) => void;
  removeAffiliatedGroup: (index: number) => void;
  addOwnershipAssignment: (assignment: OwnershipAssignment) => void;
  updateOwnershipAssignment: (id: string, patch: Partial<OwnershipAssignment>) => void;
  removeOwnershipAssignment: (id: string) => void;
  addEmployee: (employee: Employee) => void;
  updateEmployee: (index: number, employee: Employee) => void;
  removeEmployee: (index: number) => void;
  setEmployees: (employees: Employee[]) => void;
  setDocument: (docType: keyof IntakeFormData["documents"], ref: DocumentRef | null) => void;
  addOwnerEligibilityDoc: (ref: DocumentRef) => void;
  removeOwnerEligibilityDoc: (index: number) => void;
  clearOwnerEligibilityDocs: () => void;

  reset: () => void;
}

// sessionStorage keys are namespaced by formId so navigating between
// opportunities in the same tab doesn't carry step/visited state across
// forms. Pre-load values land in `loadFromApi` once we know the formId.
// Stable client-only id used as React key for virtualized employee rows. Index-based
// keys break virtualizer reconciliation when rows are removed.
const makeEmployeeUid = (): string =>
  (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `emp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const stepKey = (formId: string) => `currentStep:${formId}`;
const visitedKey = (formId: string) => `visitedSteps:${formId}`;

function readPersistedStep(formId: string, totalSteps: number): number {
  const stored = sessionStorage.getItem(stepKey(formId));
  if (stored === null) return 0;
  const parsed = Number(stored);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(parsed, totalSteps));
}

function readPersistedVisited(formId: string, totalSteps: number): Set<number> {
  const raw = sessionStorage.getItem(visitedKey(formId));
  if (raw === null) return new Set([0]);
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set([0]);
    const clamped = parsed
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n >= 0 && n <= totalSteps);
    return new Set<number>(clamped.length > 0 ? clamped : [0]);
  } catch {
    return new Set([0]);
  }
}

const initialState = {
  formId: null as string | null,
  proposalId: null as string | null,
  formVersion: null as number | null,
  formStatus: "draft" as FormStatus,
  saveStatus: "idle" as SaveStatus,
  isDirty: false,
  currentStep: 0,
  totalSteps: TOTAL_STEPS,
  visitedSteps: new Set<number>([0]),
  data: { ...EMPTY_FORM_DATA },
  prefilled: {} as Record<string, boolean>,
  originalData: {} as Record<string, unknown>,
  mismatches: {} as Record<string, Mismatch>,
  meta: { organizationTypeOptions: [], medicalCarrierOptions: [], excludedClassesOptions: [], waitingPeriodOptions: [], sicCodes: [], uwMethod: null, isRenewal: false, availablePlans: 1, ihqLink: null } as IntakeFormMeta,
};

export const useFormStore = create<FormState>()((set) => ({
  ...initialState,

  loadFromApi: (response) => {
    // originalData holds the V1 baseline (not the current value) so
    // useFormField can diff in-progress edits against it for "Differs
    // from proposal" hints.
    const original: Record<string, unknown> = {};
    for (const key of Object.keys(response.prefilled)) {
      if (response.prefilled[key]) {
        original[key] = response.baseline[key];
      }
    }
    set({
      formId: response.id,
      proposalId: response.proposalId,
      formVersion: response.version,
      formStatus: response.status,
      data: response.data,
      prefilled: response.prefilled,
      originalData: original,
      mismatches: response.mismatches,
      currentStep: readPersistedStep(response.id, TOTAL_STEPS),
      visitedSteps: new Set<number>([0, ...readPersistedVisited(response.id, TOTAL_STEPS)]),
      meta: response.meta,
      isDirty: false,
      saveStatus: "idle",
    });

    // Seed the progress bar's completed (checkmark) steps from the persisted
    // data so completion reflects the saved form for any new session or user —
    // not just the pages visited this browser session (ADO-3452). Runs after
    // the set() above so step 7's schema resolves against the freshly-stored
    // meta (uwMethod / availablePlans), not the stale initial meta.
    const completedSteps = computeCompletedSteps(response.data);
    if (completedSteps.size > 0) {
      set((state) => ({ visitedSteps: new Set<number>([...state.visitedSteps, ...completedSteps]) }));
    }
  },

  setField: (field, value) =>
    set((state) => {
      // Mismatch detection lives here so side-channel writes (zip lookup
      // populating city/state) trigger it the same as direct user input.
      const key = field as string;
      const newMismatches = { ...state.mismatches };
      if (state.prefilled[key]) {
        const originalStr = state.originalData[key] === null || state.originalData[key] === undefined
          ? ""
          : String(state.originalData[key]);
        const newStr = value === null || value === undefined ? "" : String(value);
        if (newStr !== originalStr) {
          newMismatches[key] = { original: originalStr, current: newStr };
        } else {
          delete newMismatches[key];
        }
      }
      return {
        data: { ...state.data, [field]: value },
        mismatches: newMismatches,
        isDirty: true,
        saveStatus: "idle",
      };
    }),

  setStep: (step) =>
    set((state) => {
      const clamped = Math.max(0, Math.min(step, state.totalSteps));
      const visited = new Set([...state.visitedSteps, clamped]);
      if (state.formId) {
        sessionStorage.setItem(stepKey(state.formId), String(clamped));
        sessionStorage.setItem(visitedKey(state.formId), JSON.stringify([...visited]));
      }
      return { currentStep: clamped, visitedSteps: visited };
    }),

  nextStep: () =>
    set((state) => {
      const next = Math.min(state.currentStep + 1, state.totalSteps);
      const visited = new Set([...state.visitedSteps, next]);
      if (state.formId) {
        sessionStorage.setItem(stepKey(state.formId), String(next));
        sessionStorage.setItem(visitedKey(state.formId), JSON.stringify([...visited]));
      }
      return { currentStep: next, visitedSteps: visited };
    }),

  prevStep: () =>
    set((state) => {
      const prev = Math.max(state.currentStep - 1, 0);
      if (state.formId) {
        sessionStorage.setItem(stepKey(state.formId), String(prev));
      }
      return { currentStep: prev };
    }),

  setSaveStatus: (status) => set({ saveStatus: status }),
  setFormStatus: (status) => set({ formStatus: status }),

  addOwner: (owner) =>
    set((state) => {
      // MAX_OWNERS cap — the Step 3 UI disables the Add button at the ceiling,
      // but any programmatic caller (draft restore, imported data) also gets
      // dropped past the limit so the roster stays bounded end-to-end.
      if (state.data.owners.length >= MAX_OWNERS) return state;
      return {
        data: { ...state.data, owners: [...state.data.owners, owner] },
        isDirty: true,
      };
    }),

  updateOwner: (index, owner) =>
    set((state) => ({
      data: {
        ...state.data,
        owners: state.data.owners.map((o, i) => (i === index ? owner : o)),
      },
      isDirty: true,
    })),

  // Removing an owner cascades — the shared roster is the source of truth for
  // dropdown options in every per-company Ownership Breakdown section, so any
  // assignments referring to the removed owner must go too. Otherwise an orphan
  // row would render with no name and no way to fix it.
  removeOwner: (index) =>
    set((state) => {
      const removed = state.data.owners[index];
      const owners = state.data.owners.filter((_, i) => i !== index);
      const ownershipAssignments = removed
        ? state.data.ownershipAssignments.filter((a) => a.ownerId !== removed.id)
        : state.data.ownershipAssignments;
      return {
        data: { ...state.data, owners, ownershipAssignments },
        isDirty: true,
      };
    }),

  addAffiliatedGroup: (group) =>
    set((state) => ({
      data: {
        ...state.data,
        affiliatedGroups: [...state.data.affiliatedGroups, group],
      },
      isDirty: true,
    })),

  // If the user flips an entity's Covered from Yes → No (or clears it), that
  // entity's Ownership Breakdown section hides — drop its assignments so a later
  // flip back to Yes starts clean instead of resurrecting stale rows.
  updateAffiliatedGroup: (index, group) =>
    set((state) => {
      const prev = state.data.affiliatedGroups[index];
      const affiliatedGroups = state.data.affiliatedGroups.map((g, i) => (i === index ? group : g));
      const droppedCoverage = prev?.covered === true && group.covered !== true;
      const ownershipAssignments = droppedCoverage
        ? state.data.ownershipAssignments.filter((a) => a.entityId !== group.id)
        : state.data.ownershipAssignments;
      return {
        data: { ...state.data, affiliatedGroups, ownershipAssignments },
        isDirty: true,
      };
    }),

  removeAffiliatedGroup: (index) =>
    set((state) => {
      const removed = state.data.affiliatedGroups[index];
      const affiliatedGroups = state.data.affiliatedGroups.filter((_, i) => i !== index);
      const ownershipAssignments = removed
        ? state.data.ownershipAssignments.filter((a) => a.entityId !== removed.id)
        : state.data.ownershipAssignments;
      return {
        data: { ...state.data, affiliatedGroups, ownershipAssignments },
        isDirty: true,
      };
    }),

  addOwnershipAssignment: (assignment) =>
    set((state) => ({
      data: {
        ...state.data,
        ownershipAssignments: [...state.data.ownershipAssignments, assignment],
      },
      isDirty: true,
    })),

  updateOwnershipAssignment: (id, patch) =>
    set((state) => ({
      data: {
        ...state.data,
        ownershipAssignments: state.data.ownershipAssignments.map((a) =>
          a.id === id ? { ...a, ...patch } : a,
        ),
      },
      isDirty: true,
    })),

  removeOwnershipAssignment: (id) =>
    set((state) => ({
      data: {
        ...state.data,
        ownershipAssignments: state.data.ownershipAssignments.filter((a) => a.id !== id),
      },
      isDirty: true,
    })),

  addEmployee: (employee) =>
    set((state) => ({
      data: {
        ...state.data,
        employees: [...state.data.employees, { ...employee, uid: employee.uid || makeEmployeeUid() }],
      },
      isDirty: true,
    })),

  updateEmployee: (index, employee) =>
    set((state) => ({
      data: {
        ...state.data,
        employees: state.data.employees.map((e, i) =>
          i === index ? { ...employee, uid: employee.uid || e.uid || makeEmployeeUid() } : e,
        ),
      },
      isDirty: true,
    })),

  removeEmployee: (index) =>
    set((state) => ({
      data: {
        ...state.data,
        employees: state.data.employees.filter((_, i) => i !== index),
      },
      isDirty: true,
    })),

  setEmployees: (employees) =>
    set((state) => ({
      data: {
        ...state.data,
        // Ensure every row has a uid — virtualized list keys depend on it.
        employees: employees.map((e) => (e.uid ? e : { ...e, uid: makeEmployeeUid() })),
      },
      isDirty: true,
    })),

  setDocument: (docType, ref) =>
    set((state) => ({
      data: {
        ...state.data,
        documents: { ...state.data.documents, [docType]: ref },
      },
      isDirty: true,
    })),

  addOwnerEligibilityDoc: (ref) =>
    set((state) => ({
      data: { ...state.data, ownerEligibilityDocs: [...state.data.ownerEligibilityDocs, ref] },
      isDirty: true,
    })),

  removeOwnerEligibilityDoc: (index) =>
    set((state) => ({
      data: { ...state.data, ownerEligibilityDocs: state.data.ownerEligibilityDocs.filter((_, i) => i !== index) },
      isDirty: true,
    })),

  clearOwnerEligibilityDocs: () =>
    set((state) => ({
      data: { ...state.data, ownerEligibilityDocs: [] },
      isDirty: true,
    })),

  reset: () => {
    set((state) => {
      if (state.formId) {
        sessionStorage.removeItem(stepKey(state.formId));
        sessionStorage.removeItem(visitedKey(state.formId));
      }
      return { ...initialState, currentStep: 0, visitedSteps: new Set([0]) };
    });
  },
}));

// Clear conditional fields/files when their control isn't shown, so nothing stale
// lingers in Salesforce. Stateless (clear when hidden AND populated), not a
// transition check, so it also catches a form that loads already
// hidden-but-populated (ADO-3523). Idempotent, so it won't re-save.
//
// Deletes/saves only run for the session that owns the edit lock. Without that
// gate, a read-only viewer's load could delete the editor's files (delete isn't
// lock-gated on the server), or a save could race lock acquisition into a false
// "removed" screen. The load-time pass runs when the lock is acquired, not during
// loadFromApi.

export type DocSlot = keyof IntakeFormData["documents"];

/**
 * Which uploaded files to purge when their control isn't shown: each slot maps to
 * a predicate that's true when its upload is on the form. A slot with a file but a
 * false predicate is orphaned and gets deleted.
 *
 * The reconciler and the exhaustiveness test both read this, so a new documents.*
 * slot can't silently skip cleanup: the test fails until it's categorized here or
 * excluded below.
 */
export const DOC_VISIBILITY = {
  // Shown only when the section is up and this doc-type is selected, so a
  // mismatched leftover file (e.g. a wageTax file while the type is now Payroll)
  // gets purged even while the section shows.
  wageTax: (d: IntakeFormData) => isRequiredDocsVisibleFor(d) && DOC_VERIFICATION_SLOT[d.docVerificationType] === "wageTax",
  payroll: (d: IntakeFormData) => isRequiredDocsVisibleFor(d) && DOC_VERIFICATION_SLOT[d.docVerificationType] === "payroll",
  newBusinessDocs: (d: IntakeFormData) => isRequiredDocsVisibleFor(d) && DOC_VERIFICATION_SLOT[d.docVerificationType] === "newBusinessDocs",
  priorBill: isPriorBillVisible,
  binderCheck: isBinderCheckVisible
} satisfies Partial<Record<DocSlot, (d: IntakeFormData) => boolean>>;

/** censusFile is reconciled at load in intakeApi (UW-method / Claims flow), not by a form selection. */
export const DOC_SLOTS_RECONCILED_ELSEWHERE: readonly DocSlot[] = ["censusFile"];
/** voidedCheck has no upload control anywhere in the UI, so nothing can populate it. */
export const DOC_SLOTS_WITHOUT_UPLOAD: readonly DocSlot[] = ["voidedCheck"];

/** ACH bank detail fields, cleared as a group when the ACH section isn't shown. */
const ACH_TEXT_FIELDS = ["achAccountHolderName", "achBankName", "achRoutingNumber", "achAccountNumber"] as const;

let _cleaning = false;

function deleteFile(fileId: string): void {
  void getApi().deleteFile(fileId).catch(() =>
    trackWarning("formStore: cleanup delete failed", { fileId }));
}

function reconcileHiddenFields(): void {
  // Only the lock-owning editor may mutate/persist (see the block comment above).
  if (_cleaning || useLockStore.getState().lockStatus !== "locked") return;
  const store = useFormStore.getState();
  if (store.formStatus === "submitted") return;
  _cleaning = true;
  try {
    const { data } = store;
    let didClean = false;

    // Files: delete any upload whose control is no longer shown (registry-driven).
    for (const [slot, isShown] of Object.entries(DOC_VISIBILITY)) {
      const key = slot as DocSlot;
      const doc = data.documents[key];
      if (doc && !isShown(data)) {
        deleteFile(doc.fileId);
        store.setDocument(key, null);
        didClean = true;
      }
    }

    // Non-file fields tied to a section that can hide (not part of the documents map).
    const showRequiredDocs = isRequiredDocsVisibleFor(data);
    const showOwnerEligibility = isOwnerEligibilityVisible(data.ownershipAssignments);
    const showNewlyFormedToggle = showOwnerEligibility && !showRequiredDocs;

    // docVerificationType belongs to the Required Documents section.
    if (!showRequiredDocs && data.docVerificationType) {
      store.setField("docVerificationType", "");
      didClean = true;
    }
    // "established within 90 days?" toggle only shows inside Owner Eligibility.
    if (!showNewlyFormedToggle && data.newlyFormedCompany) {
      store.setField("newlyFormedCompany", "");
      didClean = true;
    }
    // Owner Eligibility upload list (a separate array, not a documents slot).
    if (!showOwnerEligibility && data.ownerEligibilityDocs.length) {
      for (const doc of data.ownerEligibilityDocs) deleteFile(doc.fileId);
      store.clearOwnerEligibilityDocs();
      didClean = true;
    }

    // ACH bank details (sensitive): clear when not paying by ACH.
    if (!isAchDetailsVisible(data)) {
      for (const field of ACH_TEXT_FIELDS) {
        if (data[field]) { store.setField(field, ""); didClean = true; }
      }
      if (data.achAccountType !== EMPTY_FORM_DATA.achAccountType) {
        store.setField("achAccountType", EMPTY_FORM_DATA.achAccountType);
        didClean = true;
      }
    }

    // Persist cleared values to Salesforce so they don't reappear on refresh
    if (didClean) void saveCurrentDraft();
  } finally {
    _cleaning = false;
  }
}

// Live edits: reconcile after each store change (no-op unless the lock is held).
useFormStore.subscribe(() => reconcileHiddenFields());

// Load time: reconcile once the lock is acquired, so a form that loaded already
// hidden-but-populated is cleaned by its editor with the lock held.
useLockStore.subscribe((state, prev) => {
  if (state.lockStatus === "locked" && prev.lockStatus !== "locked") reconcileHiddenFields();
});
